import { z } from 'zod'

export const ANALYTICS_EXPORT_SERVER_REDACTION_POLICY =
  'aggregate_only+pii_format_status_only+samples_not_exported' as const

export const AnalyticsExportServerFormatSchema = z.enum(['csv', 'excel', 'json'])
export type AnalyticsExportServerFormat = z.infer<typeof AnalyticsExportServerFormatSchema>

export const AnalyticsExportServerRangeKindSchema = z.enum(['7d', '30d', '90d', 'custom'])
export type AnalyticsExportServerRangeKind = z.infer<typeof AnalyticsExportServerRangeKindSchema>

export const AnalyticsExportServerDateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const AnalyticsExportServerDateRangeSchema = z
  .object({
    kind: AnalyticsExportServerRangeKindSchema,
    from: AnalyticsExportServerDateOnlySchema.nullable().optional(),
    to: AnalyticsExportServerDateOnlySchema.nullable().optional(),
  })
  .strict()
  .superRefine((range, context) => {
    if (range.kind !== 'custom') return
    if (!range.from || !range.to) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'analytics_export_range_invalid',
        path: ['from'],
      })
    }
  })
export type AnalyticsExportServerDateRange = z.infer<typeof AnalyticsExportServerDateRangeSchema>

export const AnalyticsExportServerStatusSchema = z.literal('complete')
export type AnalyticsExportServerStatus = z.infer<typeof AnalyticsExportServerStatusSchema>

export const AnalyticsExportServerCustomerWorkflowSchema = z.enum([
  'all',
  'new_customer',
  'existing_customer_change',
])
export type AnalyticsExportServerCustomerWorkflow = z.infer<
  typeof AnalyticsExportServerCustomerWorkflowSchema
>

export const AnalyticsExportServerRequestSchema = z
  .object({
    format: AnalyticsExportServerFormatSchema,
    range: AnalyticsExportServerDateRangeSchema,
    status: AnalyticsExportServerStatusSchema.default('complete'),
    customerWorkflow: AnalyticsExportServerCustomerWorkflowSchema.default('all'),
  })
  .strict()
export type AnalyticsExportServerRequest = z.infer<typeof AnalyticsExportServerRequestSchema>

export const AnalyticsExportServerActorRoleSchema = z.enum([
  'super_admin',
  'admin',
  'manager',
  'sales',
  'assistant',
])
export type AnalyticsExportServerActorRole = z.infer<typeof AnalyticsExportServerActorRoleSchema>

export const AnalyticsExportServerEffectiveScopeSchema = z.enum(['workspace', 'team', 'own'])
export type AnalyticsExportServerEffectiveScope = z.infer<
  typeof AnalyticsExportServerEffectiveScopeSchema
>

export const AnalyticsExportServerJobStatusSchema = z.enum([
  'queued',
  'processing',
  'ready',
  'failed',
  'expired',
])
export type AnalyticsExportServerJobStatus = z.infer<typeof AnalyticsExportServerJobStatusSchema>

export const AnalyticsExportServerDownloadModeSchema = z.enum([
  'browser_download',
  'signed_url',
  'worker_stream',
])
export type AnalyticsExportServerDownloadMode = z.infer<
  typeof AnalyticsExportServerDownloadModeSchema
>

export const AnalyticsExportServerIsoDateTimeSchema = z.string().datetime({ offset: true })

export const AnalyticsExportServerSafeResponseInputSchema = z
  .object({
    exportJobId: z.string().uuid(),
    status: AnalyticsExportServerJobStatusSchema,
    format: AnalyticsExportServerFormatSchema,
    fileName: z.string().min(1).max(180),
    rowCount: z.number().int().nonnegative(),
    downloadMode: AnalyticsExportServerDownloadModeSchema,
    downloadUrlExpiresAt: AnalyticsExportServerIsoDateTimeSchema.optional(),
    effectiveScope: AnalyticsExportServerEffectiveScopeSchema,
    createdAt: AnalyticsExportServerIsoDateTimeSchema.optional(),
    expiresAt: AnalyticsExportServerIsoDateTimeSchema.optional(),
    safeErrorCode: z
      .string()
      .regex(/^[a-z0-9_]+$/)
      .optional(),
    retryable: z.boolean().optional(),
  })
  .strict()
export type AnalyticsExportServerSafeResponseInput = z.infer<
  typeof AnalyticsExportServerSafeResponseInputSchema
>

export type AnalyticsExportServerNormalizedDateRange = {
  kind: AnalyticsExportServerRangeKind
  from: string
  to: string
}

export type AnalyticsExportServerSafeResponse = {
  data: AnalyticsExportServerSafeResponseInput & {
    redactionPolicy: typeof ANALYTICS_EXPORT_SERVER_REDACTION_POLICY
  }
}
